import React from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import pluralize from 'pluralize';

import { Icon, IconButton, Layout, PillButton, ProductImg, Text, TextButton } from 'ds';
import { formatUsd } from 'helpers';
import { CONSUMABLES_CHECKOUT_PATH } from 'routes/paths';
import { selectProductSubscriptions } from 'store/Consumables/selectors';
import { useAppSelector } from 'store/hooks';

interface ConsumablesOnboardingAddToCartToastProps {
  closeCartToast: () => void;
  onPlusMoreItemsClick: () => void;
}

const ConsumablesOnboardingAddToCartToast: React.FC<ConsumablesOnboardingAddToCartToastProps> = ({
  closeCartToast,
  onPlusMoreItemsClick
}) => {
  const productSubscriptions = useAppSelector(selectProductSubscriptions);
  const newestProductSubscription = productSubscriptions?.reduce((newest, current) => {
    if (!newest) return current;
    const newestDate = DateTime.fromISO(newest.updated_at || '');
    const currentDate = DateTime.fromISO(current.updated_at || '');
    return currentDate > newestDate ? current : newest;
  }, null as typeof productSubscriptions[0] | null);

  const history = useHistory();
  if (!newestProductSubscription || !productSubscriptions || productSubscriptions.length === 0) return null;
  const plusMoreItems =
    productSubscriptions.length - 1 > 0
      ? `+ ${productSubscriptions.length - 1} more ${pluralize('item', productSubscriptions.length - 1)}`
      : undefined;

  return (
    <Layout padding={20} color="white" width={351} borderRadius={16} boxShadow="0 8px 24px 0 rgba(0, 0, 0, 0.1200)">
      <Layout paddingBottom={16} align="center" justify="space-between">
        <Text size="headline-xs">Added to cart!</Text>
        <IconButton
          size="md"
          name="close"
          stroke={2}
          onClick={() => {
            closeCartToast();
          }}
          type={'noBackground'}
        />
      </Layout>
      <Layout flex direction="row" gap={16} align="center">
        {newestProductSubscription?.consumables_product?.image_key ? (
          <ProductImg
            srcKey={newestProductSubscription.consumables_product.image_key}
            size="sm"
            alt={newestProductSubscription.consumables_product.detail}
            style={{ width: 72, height: 72, borderRadius: 8 }}
          />
        ) : (
          <Layout width={72} height={72} borderRadius={8} color="gray-100" align="center" justify="center">
            <Icon name="miscellaneous" color="gray-500" size={48} />
          </Layout>
        )}
        <Layout flex direction="column" gap={4} width={223} maxWidth={223}>
          <Text size="body-sm" semibold>
            {newestProductSubscription.consumables_product.brand} {newestProductSubscription.consumables_product.detail}{' '}
            {pluralize(newestProductSubscription.consumables_product.unit_type ?? '', 2)} (
            {newestProductSubscription.consumables_product.units_per_package}/
            {newestProductSubscription.consumables_product.package_type})
          </Text>
          <Text size="body-sm" semibold>
            {formatUsd(newestProductSubscription.consumables_product.amount_cents / 100)} ea.
          </Text>
          <Layout direction="row" align="center" gap={4}>
            <Text size="body-xs" color="gray-700" semibold>
              Qty:
            </Text>
            <Text size="body-xs" color="gray-700">
              {newestProductSubscription.order_quantity}
            </Text>
          </Layout>
        </Layout>
      </Layout>
      {plusMoreItems && (
        <Layout marginTop={12} paddingLeft={4}>
          <TextButton
            size="md"
            color="gray-700"
            textSize="link-md"
            text={plusMoreItems}
            underline
            onClick={onPlusMoreItemsClick}
            semibold={false}
          />
        </Layout>
      )}
      <Layout marginTop={24}>
        <PillButton
          fullWidth
          text="Go to checkout"
          size="md"
          type="purpleGradient"
          onClick={() => {
            history.push(CONSUMABLES_CHECKOUT_PATH);
          }}
          borderRadius={40}
        />
      </Layout>
    </Layout>
  );
};

export default ConsumablesOnboardingAddToCartToast;
